import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { IonPage, IonContent } from "@ionic/react";
import { Check } from "lucide-react";
import { useStore } from "../store/useStore";
import { useToast } from "../hooks/useToast";
import { ZFrequencyUnit } from "../types/zod-schemas";
import { getDifficultyColor, getDifficultyLabel } from "../logic/display-helpers";
import { PageHeader } from "../components/ui/PageHeader";
import { PrimaryButton } from "../components/ui/Button";
import { cn } from "../lib/utils";

export default function CreateTaskPage() {
  const history = useHistory();
  const { areaId } = useParams<{ areaId: string }>();

  // Form state
  const [taskName, setTaskName] = useState("");
  const [points, setPoints] = useState(2);
  const [frequencyNumber, setFrequencyNumber] = useState(1);
  const [frequencyUnit, setFrequencyUnit] = useState<
    "days" | "weeks" | "months" | "years"
  >("weeks");
  const [selectedAreaId, setSelectedAreaId] = useState(areaId || "");
  const [lastCompletedDays, setLastCompletedDays] = useState("");
  const [isHighPriority, setIsHighPriority] = useState(false);

  // Get data from Zustand store
  const currentHouseholdId = useStore((state) => state.currentHouseholdId);
  const areas = useStore((state) => state.areas);
  const createTaskStore = useStore((state) => state.createTask);
  const [isLoading, setIsLoading] = useState(false);
  const showToast = useToast((s) => s.showToast);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!taskName.trim() || !selectedAreaId || !currentHouseholdId) return;

    setIsLoading(true);
    try {
      await createTaskStore({
        name: taskName.trim(),
        points,
        frequency_number: frequencyNumber,
        frequency_unit: frequencyUnit,
        area_uuid: selectedAreaId,
        is_high_priority: isHighPriority,
      });

      showToast("Task created successfully", "success");

      // Navigate back to area detail or today page
      if (areaId) {
        history.push(`/areas/${areaId}`);
      } else {
        history.push("/today");
      }
    } catch (error) {
      console.error("Error creating task:", error);
      setIsLoading(false);
      showToast("Failed to create task. Please try again.", "danger");
    }
  };

  return (
    <IonPage>
      <PageHeader
        title="Add New Task"
        backHref={areaId ? `/areas/${areaId}` : "/today"}
      />
      <IonContent>
        <div className="max-w-lg mx-auto px-4 pt-2 pb-28">
          <form onSubmit={handleSubmit} className="space-y-7">
            {/* Task Name */}
            <div>
              <label htmlFor="task-name" className="field-label">
                Task Name
              </label>
              <input
                id="task-name"
                type="text"
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
                placeholder="e.g., Clean bathroom sink"
                className="input-field"
                required
              />
            </div>

            {/* Area Selection */}
            <div>
              <label htmlFor="task-area" className="field-label">
                Area
              </label>
              <select
                id="task-area"
                value={selectedAreaId}
                onChange={(e) => setSelectedAreaId(e.target.value)}
                className="input-field"
                required
              >
                <option value="">Select an area</option>
                {areas.map((area) => (
                  <option key={area.uuid} value={area.uuid}>
                    {area.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Effort Level */}
            <fieldset>
              <legend className="field-label">Effort Level</legend>
              <div className="grid grid-cols-1 gap-2">
                {[1, 2, 3, 4, 5].map((pointValue) => {
                  const selected = points === pointValue;
                  return (
                    <label
                      key={pointValue}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 min-h-[56px] rounded-2xl border-2 cursor-pointer transition-all",
                        "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
                        selected
                          ? "border-primary bg-accent"
                          : "border-border bg-card hover:bg-surface"
                      )}
                    >
                      <input
                        type="radio"
                        name="points"
                        value={pointValue}
                        checked={selected}
                        onChange={(e) => setPoints(parseInt(e.target.value))}
                        className="sr-only"
                      />
                      <span
                        aria-hidden="true"
                        className={`w-9 h-9 ${getDifficultyColor(
                          pointValue
                        )} rounded-full flex items-center justify-center text-white font-bold text-sm`}
                      >
                        {pointValue}
                      </span>
                      <span className="flex-1">
                        <span className="block text-foreground font-semibold">
                          {getDifficultyLabel(pointValue)}
                        </span>
                        <span className="block text-muted-foreground text-sm">
                          {pointValue} point{pointValue > 1 ? "s" : ""}
                        </span>
                      </span>
                      {selected && (
                        <Check className="w-5 h-5 text-primary" aria-hidden="true" />
                      )}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {/* Frequency */}
            <fieldset>
              <legend className="field-label">How often should this be done?</legend>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label htmlFor="task-frequency-number" className="block text-muted-foreground text-sm mb-1">
                    Every
                  </label>
                  <input
                    id="task-frequency-number"
                    type="number"
                    min="1"
                    value={frequencyNumber}
                    onChange={(e) =>
                      setFrequencyNumber(parseInt(e.target.value) || 1)
                    }
                    className="input-field"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="task-frequency-unit" className="block text-muted-foreground text-sm mb-1">
                    Unit
                  </label>
                  <select
                    id="task-frequency-unit"
                    value={frequencyUnit}
                    onChange={(e) => {
                      try {
                        const value = ZFrequencyUnit.parse(e.target.value);
                        setFrequencyUnit(value);
                      } catch {
                        // Invalid frequency unit, ignore
                      }
                    }}
                    className="input-field"
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </select>
                </div>
              </div>
              <p className="text-muted-foreground text-sm mt-2">
                This task should be done every {frequencyNumber}{" "}
                {frequencyUnit}
              </p>
            </fieldset>

            {/* When was this last done? */}
            <fieldset>
              <legend className="field-label">When was this last done?</legend>
              <div className="card-surface divide-y divide-border overflow-hidden">
                <label className="flex items-center gap-3 px-4 min-h-[52px] text-foreground cursor-pointer">
                  <input
                    type="radio"
                    name="lastCompleted"
                    checked={lastCompletedDays === ""}
                    onChange={() => setLastCompletedDays("")}
                    className="w-5 h-5 accent-primary"
                  />
                  Never (start fresh)
                </label>
                <label className="flex items-center gap-3 px-4 min-h-[52px] text-foreground cursor-pointer">
                  <input
                    type="radio"
                    name="lastCompleted"
                    checked={lastCompletedDays === "0"}
                    onChange={() => setLastCompletedDays("0")}
                    className="w-5 h-5 accent-primary"
                  />
                  Just completed (0% dirty)
                </label>
                <div className="flex items-center gap-3 px-4 min-h-[52px]">
                  <input
                    type="radio"
                    name="lastCompleted"
                    aria-label="Some days ago"
                    checked={
                      lastCompletedDays !== "" && lastCompletedDays !== "0"
                    }
                    onChange={() => setLastCompletedDays("7")}
                    className="w-5 h-5 accent-primary"
                  />
                  <input
                    type="number"
                    min="1"
                    placeholder="7"
                    aria-label="Days since last done"
                    value={
                      lastCompletedDays !== "" && lastCompletedDays !== "0"
                        ? lastCompletedDays
                        : ""
                    }
                    onChange={(e) => setLastCompletedDays(e.target.value)}
                    className="w-20 min-h-[40px] px-3 py-1.5 rounded-xl bg-surface border border-border text-foreground text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                  <span className="text-foreground">days ago</span>
                </div>
              </div>
            </fieldset>

            {/* High Priority */}
            <label className="flex items-center gap-3 p-4 card-surface cursor-pointer">
              <input
                type="checkbox"
                checked={isHighPriority}
                onChange={(e) => setIsHighPriority(e.target.checked)}
                className="w-5 h-5 rounded accent-primary"
              />
              <span>
                <span className="block text-foreground font-semibold">
                  High Priority Task
                </span>
                <span className="block text-muted-foreground text-sm">
                  Prevents this task from going past 100% dirty
                </span>
              </span>
            </label>

            {/* Submit Button */}
            <div className="pt-2">
              <PrimaryButton
                type="submit"
                disabled={isLoading || !taskName.trim() || !selectedAreaId}
              >
                {isLoading ? "Creating..." : "Create Task"}
              </PrimaryButton>
            </div>
          </form>
        </div>
      </IonContent>
    </IonPage>
  );
}
